import React from 'react';
import { CheckCircle2, Circle, Target, Trophy } from 'lucide-react';
import { Progress } from '../ui/progress';

const MilestoneProgressBar = ({
  raised = 0,
  goalAmount = 0,
  milestoneSteps = [],
  milestonesReached = [],
  goalCompleted = false,
  showLabels = true,
  size = 'default' // 'small', 'default', 'large'
}) => {
  const percentage = goalAmount > 0 ? Math.min((raised / goalAmount) * 100, 100) : 0;

  const formatCurrency = (amount) => {
    // Use compact notation for large numbers to prevent overflow
    if (amount >= 1000000) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(amount || 0);
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const sizeClasses = {
    small: { bar: 'h-2', icon: 'w-4 h-4', text: 'text-xs' },
    default: { bar: 'h-3', icon: 'w-5 h-5', text: 'text-sm' },
    large: { bar: 'h-4', icon: 'w-6 h-6', text: 'text-base' },
  };

  const sizes = sizeClasses[size] || sizeClasses.default;

  return (
    <div className="w-full" data-testid="milestone-progress-bar">
      {/* Progress Header */}
      <div className="flex justify-between items-end mb-2">
        <div>
          <p className={`font-bold text-emerald-400 ${size === 'large' ? 'text-2xl' : 'text-xl'}`}>
            {formatCurrency(raised)}
          </p>
          <p className={`text-muted-foreground ${sizes.text}`}>
            raised of {formatCurrency(goalAmount)} goal
          </p>
        </div>
        <div className="text-right">
          <span className={`font-semibold text-foreground ${size === 'large' ? 'text-xl' : 'text-lg'}`}>
            {Math.round(percentage)}%
          </span>
          {goalCompleted && (
            <span className="ml-2 inline-flex items-center">
              <Trophy className="w-4 h-4 text-yellow-400" />
            </span>
          )}
        </div>
      </div>

      {/* Progress Bar with Milestone Markers */}
      <div className="relative">
        <Progress value={percentage} className={`${sizes.bar} bg-secondary`} />
        
        {/* Milestone Markers */}
        {milestoneSteps.length > 0 && (
          <div className="absolute inset-0 flex items-center">
            {milestoneSteps.map((milestone, index) => {
              const position = goalAmount > 0 ? (milestone / goalAmount) * 100 : 0;
              const isReached = milestonesReached.includes(milestone);
              const isGoal = milestone === goalAmount;
              
              return (
                <div
                  key={index}
                  className="absolute transform -translate-x-1/2"
                  style={{ left: `${Math.min(position, 100)}%` }}
                >
                  {isGoal ? (
                    <Target
                      className={`${sizes.icon} ${
                        isReached ? 'text-yellow-400' : 'text-muted-foreground'
                      }`}
                    />
                  ) : isReached ? (
                    <CheckCircle2 className={`${sizes.icon} text-emerald-400`} />
                  ) : (
                    <Circle className={`${sizes.icon} text-muted-foreground/50`} />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Milestone Labels */}
      {showLabels && milestoneSteps.length > 0 && (
        <div className="flex justify-between items-center mt-3 px-1">
          {milestoneSteps.map((milestone, index) => {
            const isReached = milestonesReached.includes(milestone);
            const isGoal = milestone === goalAmount;
            
            return (
              <div
                key={index}
                className="text-center flex-1"
              >
                <span
                  className={`${sizes.text} whitespace-nowrap ${
                    isReached
                      ? isGoal
                        ? 'text-yellow-400 font-semibold'
                        : 'text-emerald-400'
                      : 'text-muted-foreground'
                  }`}
                >
                  {formatCurrency(milestone)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Milestone Summary */}
      {milestonesReached.length > 0 && (
        <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
          <CheckCircle2 className="w-3 h-3 text-emerald-400" />
          <span>
            {milestonesReached.length} of {milestoneSteps.length} milestones reached
          </span>
        </div>
      )}
    </div>
  );
};

export default MilestoneProgressBar;
